/**
 * Shared constants for the projects feature module
 * Centralizes magic numbers, validation patterns, and error messages
 */

export const PROJECT_CONSTANTS = {
  // Project name validation
  MIN_NAME_LENGTH: 3,
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 150,

  // File size limits (bytes) - aligned with model payload limits
  MAX_DOC_SIZE_BYTES: 4.5 * 1024 * 1024, // 4.5MB for documents
  MAX_DOC_SIZE_MB: 4.5,
  MAX_IMAGE_SIZE_BYTES: 3.75 * 1024 * 1024, // 3.75MB for images
  MAX_IMAGE_SIZE_MB: 3.75,
  MAX_TOTAL_PAYLOAD_MB: 20,

  // Legacy (kept for backward compat with other upload flows)
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
  MAX_FILE_SIZE_MB: 10,

  // Upload file size limit (5MB for individual uploads)
  MAX_UPLOAD_FILE_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  MAX_UPLOAD_FILE_SIZE_MB: 5,

  // Image file extensions
  IMAGE_EXTENSIONS: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],

  // Validation patterns
  INVALID_CHARS_REGEX: /[<>:"/\\|?*]/,
  INVALID_CHARS_MESSAGE: 'Name contains invalid characters: < > : " / \\ | ? *',

  // Folder name validation (only letters, numbers, spaces, and underscores)
  FOLDER_MAX_NAME_LENGTH: 250,
  FOLDER_ALLOWED_CHARS_REGEX: /^[a-zA-Z0-9_ ]+$/,

  // Storage keys
  STORAGE_KEYS: {
    CURRENT_PROJECT_NAME: 'currentProjectName',
    CURRENT_PROJECT_ID: 'currentProjectId'
  },

  // Error messages
  ERRORS: {
    NAME_TOO_SHORT: (min) => `Name must be at least ${min} characters`,
    NAME_TOO_LONG: (max) => `Name must not exceed ${max} characters`,
    NAME_RANGE: (min, max) => `Name must be between ${min} and ${max} characters`,
    DESCRIPTION_TOO_LONG: (max) => `Description must not exceed ${max} characters`,
    FILE_TOO_LARGE: (maxMB) => `File size must be less than ${maxMB}MB`,
    INVALID_CHARACTERS: 'Name contains invalid characters: < > : " / \\ | ? *',
    FOLDER_INVALID_CHARACTERS: 'Only letters, numbers, spaces, and underscores are allowed',
    FOLDER_NAME_TOO_LONG: (max) => `Name must not exceed ${max} characters`,
    NAME_ALREADY_EXISTS: 'A folder with this name already exists',
    GENERIC_ERROR: 'An error occurred. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection and try again.'
  }
};

/**
 * Validates project or folder name
 * @param {string} name - The name to validate
 * @param {number} minLength - Minimum length (default: 3)
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateName = (name, minLength = PROJECT_CONSTANTS.MIN_NAME_LENGTH, maxLength = PROJECT_CONSTANTS.MAX_NAME_LENGTH) => {
  if (!name || name.trim().length === 0) {
    return 'Name is required';
  }

  const trimmedName = name.trim();

  if (trimmedName.length < minLength) {
    return PROJECT_CONSTANTS.ERRORS.NAME_TOO_SHORT(minLength);
  }

  if (trimmedName.length > maxLength) {
    return PROJECT_CONSTANTS.ERRORS.NAME_TOO_LONG(maxLength);
  }

  if (PROJECT_CONSTANTS.INVALID_CHARS_REGEX.test(trimmedName)) {
    return PROJECT_CONSTANTS.ERRORS.INVALID_CHARACTERS;
  }

  return null; // Valid
};

/**
 * Validates folder name (stricter rules for test suites, etc.)
 * Only allows letters, numbers, and underscores. Max 250 characters.
 * @param {string} name - The folder name to validate
 * @param {string[]} [existingNames] - Optional list of existing folder names to check for duplicates
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateFolderName = (name, existingNames = []) => {
  if (!name || name.trim().length === 0) {
    return 'Name is required';
  }

  const trimmedName = name.trim();

  if (trimmedName.length < PROJECT_CONSTANTS.MIN_NAME_LENGTH) {
    return PROJECT_CONSTANTS.ERRORS.NAME_TOO_SHORT(PROJECT_CONSTANTS.MIN_NAME_LENGTH);
  }

  if (trimmedName.length > PROJECT_CONSTANTS.FOLDER_MAX_NAME_LENGTH) {
    return PROJECT_CONSTANTS.ERRORS.FOLDER_NAME_TOO_LONG(PROJECT_CONSTANTS.FOLDER_MAX_NAME_LENGTH);
  }

  if (!PROJECT_CONSTANTS.FOLDER_ALLOWED_CHARS_REGEX.test(trimmedName)) {
    return PROJECT_CONSTANTS.ERRORS.FOLDER_INVALID_CHARACTERS;
  }

  if (existingNames.length > 0) {
    const lowerName = trimmedName.toLowerCase();
    const isDuplicate = existingNames.some(
      existing => existing.replace(/\/$/, '').toLowerCase() === lowerName
    );
    if (isDuplicate) {
      return PROJECT_CONSTANTS.ERRORS.NAME_ALREADY_EXISTS;
    }
  }

  return null;
};

/**
 * Validates project description
 * @param {string} description - The description to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateDescription = (description) => {
  if (!description) {
    return null; // Description is optional
  }

  if (description.length > PROJECT_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
    return PROJECT_CONSTANTS.ERRORS.DESCRIPTION_TOO_LONG(PROJECT_CONSTANTS.MAX_DESCRIPTION_LENGTH);
  }

  return null; // Valid
};

/**
 * Validates file size based on file type (document vs image)
 * @param {number} sizeInBytes - File size in bytes
 * @param {string} [fileName] - File name to determine type-specific limits
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateFileSize = (sizeInBytes, fileName) => {
  if (fileName) {
    const lowerName = fileName.toLowerCase();
    const isImage = PROJECT_CONSTANTS.IMAGE_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    const maxBytes = isImage ? PROJECT_CONSTANTS.MAX_IMAGE_SIZE_BYTES : PROJECT_CONSTANTS.MAX_DOC_SIZE_BYTES;
    const maxMB = isImage ? PROJECT_CONSTANTS.MAX_IMAGE_SIZE_MB : PROJECT_CONSTANTS.MAX_DOC_SIZE_MB;
    const fileType = isImage ? 'images' : 'documents';

    if (sizeInBytes > maxBytes) {
      return `File size must be less than ${maxMB} MB for ${fileType}`;
    }
    return null;
  }

  // Fallback: generic 10MB limit when no fileName provided
  if (sizeInBytes > PROJECT_CONSTANTS.MAX_FILE_SIZE_BYTES) {
    return PROJECT_CONSTANTS.ERRORS.FILE_TOO_LARGE(PROJECT_CONSTANTS.MAX_FILE_SIZE_MB);
  }

  return null; // Valid
};

/**
 * Gets user-friendly error message from API error
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Check for network errors
  if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
    return PROJECT_CONSTANTS.ERRORS.NETWORK_ERROR;
  }

  // Check for API response error message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Check for error message property
  if (error.message) {
    return error.message;
  }

  // Default generic error
  return PROJECT_CONSTANTS.ERRORS.GENERIC_ERROR;
};
